/**
 * Reminders inside the Bruce window (Satya's picks, 2026-09-16).
 *
 *   ActionCards   — the cards under an answer. A reminder, a parent follow-up
 *                   or a cancel that Bruce PROPOSED: nothing is saved until the
 *                   person taps the orange button. And a reminder that fell
 *                   due: Done / in 1 hour / tomorrow.
 *   RemindersPanel — "My reminders", opened from the ⋯ menu. A coming-up
 *                   reminder can be edited in place (Satya, 2026-09-19):
 *                   your own — words, day, time; a family follow-up — the day,
 *                   which also moves the family's follow-up date.
 *
 * The server owns every rule (oh_assistant services/reminders.py); these only
 * send the tap and show what came back.
 */
import { makeStyles } from '@material-ui/core/styles'
import React, { useCallback, useEffect, useState } from 'react'

import { actOnBruceReminder, fetchBruceReminders, sendBruceAction } from 'api'
import { radius } from 'app/theme/tokens'

const ORANGE = '#F25E35'
const GREEN = '#2F7A4F'

const useStyles = makeStyles(() => ({
  card: {
    border: '1px solid #F3D3C6',
    background: '#FFFBF8',
    borderRadius: 10,
    padding: '8px 10px',
    marginTop: 6,
    whiteSpace: 'normal'
  },
  dueCard: {
    border: '1px solid #EFD58F',
    background: '#FFFCF3'
  },
  title: {
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    color: '#C2461F',
    marginBottom: 4
  },
  dueTitle: { color: '#8A6410' },
  grid: {
    display: 'grid',
    gridTemplateColumns: '78px 1fr',
    gap: '2px 8px',
    fontSize: 12.5
  },
  key: { color: '#7A7263' },
  strong: { fontWeight: 700, fontSize: 13 },
  small: { fontSize: 11.5, color: '#7A7263', marginTop: 4 },
  buttons: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 },
  btn: {
    font: 'inherit',
    fontSize: 12,
    borderRadius: radius.pill,
    padding: '4px 11px',
    border: '1px solid #E4DACA',
    background: '#FFF',
    color: '#4A4438',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    '&:hover': { borderColor: '#C9BFAE', color: '#282E3C' },
    '&:disabled': { opacity: 0.6, cursor: 'default' }
  },
  primary: {
    background: ORANGE,
    borderColor: ORANGE,
    color: '#FFF',
    fontWeight: 700,
    '&:hover': { background: '#DE4F28', borderColor: '#DE4F28', color: '#FFF' }
  },
  done: {
    background: '#E8F4EC',
    borderColor: '#BFDCC9',
    color: GREEN,
    fontWeight: 600,
    '&:hover': { borderColor: GREEN, color: GREEN }
  },
  ok: { fontSize: 12.5, color: GREEN },
  muted: { fontSize: 12.5, color: '#7A7263' },
  error: { fontSize: 11.5, color: '#B3261E', marginTop: 4 },
  link: { color: ORANGE, fontWeight: 600, fontSize: 12 },
  panel: {
    flex: 1,
    overflowY: 'auto',
    padding: 12,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    background: '#FFF'
  },
  group: {
    fontSize: 10.5,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: '#7A7263',
    marginTop: 4
  },
  item: {
    border: '1px solid #EFE6D8',
    borderRadius: 9,
    padding: '7px 9px',
    fontSize: 12.5
  },
  itemRow: { display: 'flex', gap: 8, alignItems: 'flex-start' },
  itemText: { flex: 1 },
  when: { fontSize: 11, color: '#7A7263' },
  textBtn: {
    font: 'inherit',
    fontSize: 11.5,
    color: '#2D5E93',
    textDecoration: 'underline',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    whiteSpace: 'nowrap'
  },
  empty: { fontSize: 12.5, color: '#4A4438', lineHeight: 1.5 },
  editing: { border: `1.5px solid ${ORANGE}` },
  field: {
    font: 'inherit',
    fontSize: 12.5,
    width: '100%',
    boxSizing: 'border-box',
    border: '1px solid #D9D2C5',
    borderRadius: 8,
    padding: '6px 8px',
    marginTop: 6,
    background: '#FFF',
    color: '#282E3C'
  },
  pair: { display: 'flex', gap: 6 },
  example: {
    font: 'inherit',
    fontSize: 11.5,
    marginTop: 6,
    padding: '4px 10px',
    borderRadius: radius.pill,
    border: '1px solid #E4DACA',
    background: '#FFF',
    color: '#4A4438',
    cursor: 'pointer',
    '&:hover': { borderColor: ORANGE, color: ORANGE }
  }
}))

const EXAMPLE = 'Remind me tomorrow at 11 to order chart paper'

const failed = (data) =>
  (data && data.message) || 'That did not go through. Please try again.'

// ---------------------------------------------------------------------------
// A card Bruce proposed
// ---------------------------------------------------------------------------

const ProposalCard = ({ action, messageUuid, onChange, onRetype }) => {
  const classes = useStyles()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const send = useCallback(
    async (op) => {
      setBusy(true)
      setError('')
      try {
        const data = await sendBruceAction(messageUuid, action.id, op)
        if (data && data.success) onChange(data.action)
        else setError(failed(data))
      } catch (e) {
        setError(failed())
      } finally {
        setBusy(false)
      }
    },
    [action.id, messageUuid, onChange]
  )

  const retype = useCallback(
    async (prefill) => {
      await send('dismiss')
      onRetype(prefill)
    },
    [send, onRetype]
  )

  const { kind, state } = action
  if (state === 'saved') {
    const line =
      kind === 'follow_up'
        ? `Follow-up set for ${action.when}. I'll nudge you that morning on Slack and here.`
        : `Saved. I'll remind you ${action.when} on Slack and here.`
    return <div className={classes.ok}>✓ {line}</div>
  }
  if (state === 'cancelled') {
    return <div className={classes.ok}>✓ Cancelled: {action.text}</div>
  }
  if (state === 'dismissed') {
    return <div className={classes.muted}>Not saved.</div>
  }
  if (state === 'expired') {
    return (
      <div className={classes.muted}>
        That time passed before it was saved. Ask me again with a new time.
      </div>
    )
  }

  let title
  let rows
  let saveLabel
  let skipLabel
  let changeLabel = ''
  let changePrefill = ''
  if (kind === 'follow_up') {
    title = 'Follow-up, not set yet'
    const children = (action.children || []).join(', ')
    rows = [
      [
        'Parent',
        `${action.parent}${children ? `, parent of ${children}` : ''}${
          action.centre ? ` · ${action.centre}` : ''
        }`
      ],
      ['Last talk', action.last_talk],
      [
        'Follow-up',
        `${action.when}${
          action.current_follow_up
            ? ` (today it says ${action.current_follow_up})`
            : ' (none set today)'
        }`
      ],
      ['Reaches you', 'Slack and here, that morning']
    ]
    saveLabel = 'Set follow-up'
    skipLabel = "Don't set"
    changeLabel = 'Change date'
    changePrefill = `Set ${action.parent}'s follow-up for `
  } else if (kind === 'cancel') {
    title = 'Cancel this reminder?'
    rows = [
      ['Reminder', action.text],
      ['Due', action.when]
    ]
    saveLabel = 'Cancel it'
    skipLabel = 'Keep it'
  } else {
    title = 'Reminder, not saved yet'
    rows = [
      ['What', action.text],
      ['When', action.when],
      ['Reaches you', 'Slack message, and here in Bruce']
    ]
    saveLabel = 'Save reminder'
    skipLabel = "Don't save"
    changeLabel = 'Change time'
    changePrefill = `Remind me to ${action.text
      .charAt(0)
      .toLowerCase()}${action.text.slice(1)} at `
  }

  return (
    <div className={classes.card}>
      <div className={classes.title}>{title}</div>
      <div className={classes.grid}>
        {rows.map(([k, v]) => (
          <React.Fragment key={k}>
            <span className={classes.key}>{k}</span>
            <span>{v}</span>
          </React.Fragment>
        ))}
      </div>
      <div className={classes.buttons}>
        <button
          type="button"
          className={`${classes.btn} ${classes.primary}`}
          disabled={busy}
          onClick={() => send('save')}
        >
          {saveLabel}
        </button>
        {changeLabel && (
          <button
            type="button"
            className={classes.btn}
            disabled={busy}
            onClick={() => retype(changePrefill)}
          >
            {changeLabel}
          </button>
        )}
        <button
          type="button"
          className={classes.btn}
          disabled={busy}
          onClick={() => send('dismiss')}
        >
          {skipLabel}
        </button>
      </div>
      {kind === 'follow_up' && (
        <div className={classes.small}>
          The centre team also sees this date in their follow-ups, and it counts
          as overdue if nobody logs the call.
        </div>
      )}
      {error && <div className={classes.error}>{error}</div>}
    </div>
  )
}

// ---------------------------------------------------------------------------
// A reminder that fell due
// ---------------------------------------------------------------------------

const DueCard = ({ action, onChange, onDueCount }) => {
  const classes = useStyles()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const act = useCallback(
    async (op) => {
      setBusy(true)
      setError('')
      try {
        const data = await actOnBruceReminder(action.reminder, op)
        if (data && data.success) {
          const row = data.reminder || {}
          if (op === 'done') onChange({ ...action, state: 'done' })
          else if (op === 'cancel') onChange({ ...action, state: 'cancelled' })
          else onChange({ ...action, state: 'snoozed', until: row.when })
          if (typeof data.due === 'number') onDueCount(data.due)
        } else {
          setError(failed(data))
        }
      } catch (e) {
        setError(failed())
      } finally {
        setBusy(false)
      }
    },
    [action, onChange, onDueCount]
  )

  const followUp = action.reminder_kind === 'follow_up'
  const title = followUp ? 'Follow-up due today' : 'Reminder due'

  return (
    <div className={`${classes.card} ${classes.dueCard}`}>
      <div className={`${classes.title} ${classes.dueTitle}`}>{title}</div>
      <div className={classes.strong}>{action.text}</div>
      <div className={classes.small}>
        You set this on {action.set_on}. It was due {action.due}.
      </div>
      {action.state === 'done' && <div className={classes.ok}>✓ Done.</div>}
      {action.state === 'cancelled' && (
        <div className={classes.muted}>Cancelled.</div>
      )}
      {action.state === 'snoozed' && (
        <div className={classes.muted}>
          I&apos;ll remind you again {action.until}.
        </div>
      )}
      {action.state === 'pending' && (
        <div className={classes.buttons}>
          <button
            type="button"
            className={`${classes.btn} ${classes.done}`}
            disabled={busy}
            onClick={() => act('done')}
          >
            Done
          </button>
          <button
            type="button"
            className={classes.btn}
            disabled={busy}
            onClick={() => act('hour')}
          >
            Remind me in 1 hour
          </button>
          <button
            type="button"
            className={classes.btn}
            disabled={busy}
            onClick={() => act('tomorrow')}
          >
            Tomorrow
          </button>
          {followUp && action.parent_link && (
            <a
              className={classes.link}
              href={action.parent_link}
              target="_blank"
              rel="noopener noreferrer"
              style={{ alignSelf: 'center' }}
            >
              Open the family
            </a>
          )}
        </div>
      )}
      {error && <div className={classes.error}>{error}</div>}
    </div>
  )
}

export const ActionCards = ({
  actions,
  messageUuid,
  onActionChange,
  onRetype,
  onDueCount
}) => {
  if (!actions || !actions.length) return null
  return actions.map((action) =>
    action.kind === 'due_reminder' ? (
      <DueCard
        key={action.id}
        action={action}
        onChange={(next) => onActionChange(messageUuid, next)}
        onDueCount={onDueCount}
      />
    ) : (
      <ProposalCard
        key={action.id}
        action={action}
        messageUuid={messageUuid}
        onChange={(next) => onActionChange(messageUuid, next)}
        onRetype={onRetype}
      />
    )
  )
}

// ---------------------------------------------------------------------------
// My reminders
// ---------------------------------------------------------------------------

export const RemindersPanel = ({ onAsk, onDueCount }) => {
  const classes = useStyles()
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState('')
  // The one coming-up reminder open for editing: { uuid, text, date, time, error }.
  const [draft, setDraft] = useState(null)

  const load = useCallback(async () => {
    try {
      const res = await fetchBruceReminders()
      setData(res)
      setError('')
      if (res && typeof res.due === 'number') onDueCount(res.due)
    } catch (e) {
      setError('Could not load your reminders. Please try again.')
    }
  }, [onDueCount])

  useEffect(() => {
    load()
  }, [load])

  const act = useCallback(
    async (uuid, op) => {
      setBusy(uuid)
      try {
        const res = await actOnBruceReminder(uuid, op)
        if (!res || !res.success) setError(failed(res))
        await load()
      } catch (e) {
        setError(failed())
      } finally {
        setBusy('')
      }
    },
    [load]
  )

  const startEdit = (r) =>
    setDraft({
      uuid: r.uuid,
      text: r.text,
      date: r.due_date,
      time: r.due_time,
      error: ''
    })

  const saveEdit = async () => {
    const { uuid, text, date, time } = draft
    setBusy(uuid)
    try {
      const res = await actOnBruceReminder(uuid, 'edit', { text, date, time })
      if (res && res.success) {
        setDraft(null)
        await load()
      } else {
        setDraft((d) => d && { ...d, error: failed(res) })
      }
    } catch (e) {
      setDraft((d) => d && { ...d, error: failed() })
    } finally {
      setBusy('')
    }
  }

  const editForm = (r) => {
    const followUp = r.kind === 'follow_up'
    const set = (key) => (e) => {
      const { value } = e.target
      setDraft((d) => ({ ...d, [key]: value, error: '' }))
    }
    return (
      <div key={r.uuid} className={`${classes.item} ${classes.editing}`}>
        {followUp ? (
          <div>📞 {r.text}</div>
        ) : (
          <input
            className={classes.field}
            value={draft.text}
            maxLength={300}
            onChange={set('text')}
            aria-label="What to remind you about"
          />
        )}
        <div className={classes.pair}>
          <input
            type="date"
            className={classes.field}
            value={draft.date}
            onChange={set('date')}
            aria-label="Day"
          />
          {!followUp && (
            <input
              type="time"
              className={classes.field}
              value={draft.time}
              onChange={set('time')}
              aria-label="Time"
            />
          )}
        </div>
        <div className={classes.small}>
          {followUp
            ? "Also moves this family's follow-up date on the Interactions page. I'll nudge you at 9:30 am that day."
            : 'Still comes to Slack and here.'}
        </div>
        {draft.error && <div className={classes.error}>{draft.error}</div>}
        <div className={classes.buttons} style={{ justifyContent: 'flex-end' }}>
          <button
            type="button"
            className={classes.btn}
            disabled={busy === r.uuid}
            onClick={() => setDraft(null)}
          >
            Keep as it was
          </button>
          <button
            type="button"
            className={`${classes.btn} ${classes.primary}`}
            disabled={busy === r.uuid}
            onClick={saveEdit}
          >
            Save
          </button>
        </div>
      </div>
    )
  }

  if (error && !data) return <div className={classes.panel}>{error}</div>
  if (!data) return <div className={classes.panel}>Loading…</div>

  const dueNow = data.due_now || []
  const comingUp = data.coming_up || []
  const recent = data.done_last_7_days || []

  const parentLink = (r) =>
    r.parent_link ? (
      <a
        className={classes.textBtn}
        href={r.parent_link}
        target="_blank"
        rel="noopener noreferrer"
      >
        Open
      </a>
    ) : null

  return (
    <div className={classes.panel}>
      {error && <div className={classes.error}>{error}</div>}
      {!dueNow.length && !comingUp.length && !recent.length && (
        <div className={classes.empty}>
          You have no reminders yet. Ask me in your own words and I&apos;ll show
          you a card to save. For example:
          <div>
            <button
              type="button"
              className={classes.example}
              onClick={() => onAsk(EXAMPLE)}
            >
              {EXAMPLE}
            </button>
          </div>
        </div>
      )}

      {dueNow.length > 0 && <div className={classes.group}>Due now</div>}
      {dueNow.map((r) => (
        <div key={r.uuid} className={classes.item}>
          <div className={classes.itemRow}>
            <div className={classes.itemText}>
              {r.text}
              <div className={classes.when}>Was due {r.when}</div>
            </div>
            {parentLink(r)}
          </div>
          <div className={classes.buttons} style={{ marginTop: 5 }}>
            <button
              type="button"
              className={`${classes.btn} ${classes.done}`}
              disabled={busy === r.uuid}
              onClick={() => act(r.uuid, 'done')}
            >
              Done
            </button>
            <button
              type="button"
              className={classes.btn}
              disabled={busy === r.uuid}
              onClick={() => act(r.uuid, 'hour')}
            >
              In 1 hour
            </button>
            <button
              type="button"
              className={classes.btn}
              disabled={busy === r.uuid}
              onClick={() => act(r.uuid, 'tomorrow')}
            >
              Tomorrow
            </button>
          </div>
        </div>
      ))}

      {comingUp.length > 0 && <div className={classes.group}>Coming up</div>}
      {comingUp.map((r) =>
        draft && draft.uuid === r.uuid ? (
          editForm(r)
        ) : (
          <div key={r.uuid} className={classes.item}>
            <div className={classes.itemRow}>
              <div className={classes.itemText}>
                {r.kind === 'follow_up' ? '📞 ' : ''}
                {r.text}
                <div className={classes.when}>
                  {r.when} ·{' '}
                  {r.kind === 'follow_up'
                    ? 'follow-up on the interaction'
                    : 'Slack and here'}
                </div>
              </div>
              {parentLink(r)}
              <button
                type="button"
                className={classes.textBtn}
                disabled={busy === r.uuid}
                onClick={() => startEdit(r)}
              >
                Edit
              </button>
              <button
                type="button"
                className={classes.textBtn}
                disabled={busy === r.uuid}
                onClick={() => act(r.uuid, 'cancel')}
              >
                Cancel
              </button>
            </div>
          </div>
        )
      )}

      {recent.length > 0 && (
        <div className={classes.group}>Done or cancelled, last 7 days</div>
      )}
      {recent.map((r) => (
        <div key={r.uuid} className={classes.item} style={{ opacity: 0.7 }}>
          {r.text}
          <div className={classes.when}>
            {r.status_label} · was due {r.when}
          </div>
        </div>
      ))}
    </div>
  )
}
